/**
 * O extrato para faturamento — o que o parceiro precisa para emitir a NOTA DELE.
 *
 * ## O que o modal responde, e por que ele existe
 *
 * "Pronto para faturar" é um estado, não uma instrução. Quem está do outro lado precisa
 * de três coisas para agir: **quanto**, **contra quem** e **até quando**. As três estão
 * aqui, e só entram as comissões cuja parcela do cliente JÁ QUITOU — a liberação da D-174 (DS-618), spec 05
 * §7, e não uma seleção da tela.
 *
 * ## O documento sai da PRÓPRIA tela, e não de um PDF do servidor (DS-386)
 *
 * A spec 11.1 prevê `POST /portal/parceiro/extrato/pdf` com URL assinada. O caminho
 * está fechado por uma razão de fonte de verdade, não de privilégio: o job que existe
 * (`FILA_PDF_EXTRATO_COMISSAO`) exige o **extrato canônico inteiro**, que nasce do
 * endpoint interno com filtro de permissão e mascaramento por nível de usuário.
 * Recalculá-lo para um usuário externo criaria uma SEGUNDA fonte para o número que o
 * parceiro usa para nos faturar — e duas fontes para o mesmo dinheiro é o defeito que
 * este repositório mais evita.
 *
 * O que existe no lugar resolve a necessidade real ("preciso anexar isto à minha nota")
 * sem criar a segunda fonte: a folha de impressão desta tela. O documento sai dos
 * MESMOS números que a pessoa está vendo, sem servidor, sem armazenamento e sem ciclo
 * de vida novo para um documento financeiro num acervo externo. Não é o PDF com papel
 * timbrado da 11.1 — e o botão diz "Salvar em PDF", que é o que ele faz.
 *
 * ## Uma nota por GRUPO, e o grupo é (empresa beneficiária, contra quem emitir)
 *
 * Cada empresa do portal emite a SUA nota (D-131), e cada nota vai contra um destinatário.
 * Por isso as linhas se agrupam por empresa e, dentro dela, por "Emitir contra", cada grupo
 * com o seu subtotal e o seu vencimento — um `<tbody>` por grupo, na MESMA tabela, com UM
 * total geral no rodapé. O "Emitir contra" e o vencimento saíam da primeira linha da lista,
 * e bastava uma linha com outro destinatário para o extrato mandar emitir a nota errada.
 */
import { Download, X } from 'lucide-react'
import { ModalDoPortal } from '../componentes/modal.js'
import type { LinhaDoExtratoDoParceiroNoPortal } from '@dv/contracts'
import { useEmpresas } from '../estado/empresas.js'
import { br, dataBr, percentualBr, somar } from '../formato.js'

interface GrupoDoExtrato {
  readonly chave: string
  readonly empresaId: string
  readonly contraQuemEmitir: string
  readonly linhas: LinhaDoExtratoDoParceiroNoPortal[]
}

/** Os grupos na ORDEM da API (empresa, contra quem, vencimento): a tela não reordena. */
function agrupar(linhas: readonly LinhaDoExtratoDoParceiroNoPortal[]): GrupoDoExtrato[] {
  const grupos = new Map<string, GrupoDoExtrato>()
  for (const linha of linhas) {
    const chave = `${linha.empresaId}|${linha.contraQuemEmitir}`
    const grupo = grupos.get(chave)
    if (grupo === undefined) {
      grupos.set(chave, { chave, empresaId: linha.empresaId, contraQuemEmitir: linha.contraQuemEmitir, linhas: [linha] })
    } else {
      grupo.linhas.push(linha)
    }
  }
  return [...grupos.values()]
}

/** "10/08/2026" · "10/08/2026 e 20/08/2026": todos os vencimentos do grupo, nenhum escolhido. */
function vencimentosDe(linhas: readonly LinhaDoExtratoDoParceiroNoPortal[]): { readonly rotulo: string; readonly datas: string } {
  const datas = [...new Set(linhas.map((linha) => linha.liberadaVencimento))].sort().map(dataBr)
  const ultima = datas.at(-1)
  if (ultima === undefined) return { rotulo: 'Vencimento acordado', datas: '—' }
  if (datas.length === 1) return { rotulo: 'Vencimento acordado', datas: ultima }
  return { rotulo: 'Vencimentos acordados', datas: `${datas.slice(0, -1).join(', ')} e ${ultima}` }
}

export function ExtratoParaFaturamento({
  linhas,
  aoFechar,
}: {
  linhas: readonly LinhaDoExtratoDoParceiroNoPortal[]
  aoFechar: () => void
}) {
  const empresas = useEmpresas()
  const grupos = agrupar(linhas)
  const variosGrupos = grupos.length > 1
  const total = somar(linhas.map((l) => l.liberadaValor))

  return (
    /*
      O `Modal` do app interno (sobreposicao.tsx:242, :279-311; D-134 · DS-531): moldura única,
      672px porque tem tabela, título de 17px sem eyebrow, o X redondo e o apoio logo abaixo da
      cabeça, também no vazio. A frase que morava no pé virou o apoio.
    */
    <ModalDoPortal rotulo="Extrato para faturamento" aoFechar={aoFechar} teste="modal-extrato" testeDoVeu="veu-do-extrato" largo>
        <div className="modal-cabeca">
          <h2>Extrato para faturamento</h2>
          <button type="button" className="modal-fechar" onClick={aoFechar} aria-label="Fechar">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <p className="apoio-do-modal" data-teste="apoio-do-extrato">
          Só entram aqui as comissões cujo cliente já pagou, é o que libera a sua parte.
          {variosGrupos && ' Cada grupo é uma nota: emita uma por grupo, contra quem ele indica.'}
        </p>

        {linhas.length === 0 ? (
          // O vazio tracejado do app interno (contatos.tsx:127-129), com o texto da spec 11.1:108.
          <p className="vazio-do-modal" role="status">
            Nenhuma comissão liberada no momento. Assim que um cliente pagar, o valor aparece aqui.
          </p>
        ) : (
          <div className="tabela dv-xscroll">
            <table className="dv-tabela" data-teste="tabela-extrato-para-faturamento">
              <thead>
                <tr>
                  <th className="dv-elastica">Pedido + cliente</th>
                  <th>Base</th>
                  <th>%</th>
                  <th>Comissão</th>
                </tr>
              </thead>
              {grupos.map((grupo) => {
                const vencimentos = vencimentosDe(grupo.linhas)
                return (
                  <tbody key={grupo.chave} data-teste="grupo-do-extrato">
                    {/* A cabeça do grupo ocupa a linha inteira: é a instrução da nota, não um valor. */}
                    <tr className="linha-grupo">
                      <td colSpan={4}>
                        <div className="cabeca-do-grupo">
                          {empresas.mostrar && (
                            <strong data-teste="empresa-do-grupo">{empresas.nomeDe(grupo.empresaId)}</strong>
                          )}
                          <span data-teste="emitir-contra">
                            <strong>Emitir contra</strong> · {grupo.contraQuemEmitir}
                          </span>
                          <span>
                            <strong>{vencimentos.rotulo}</strong> · {vencimentos.datas}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {grupo.linhas.map((linha, indice) => (
                      <tr key={`${grupo.chave}-${linha.pedidoCodigo}-${linha.liberadaVencimento}-${String(indice)}`} data-teste="linha-extrato">
                        <td className="dv-elastica">
                          <span className="codigo tnum">{linha.pedidoCodigo}</span>
                          <span className="linha-apoio">{linha.clienteNome}</span>
                        </td>
                        <td className="tnum texto-secundario">{br(linha.baseValor)}</td>
                        <td className="tnum">{linha.percentual === null ? '—' : percentualBr(linha.percentual)}</td>
                        <td className="tnum valor">{br(linha.liberadaValor)}</td>
                      </tr>
                    ))}
                    {/* Com um grupo só, o subtotal repetiria o total logo abaixo. */}
                    {variosGrupos && (
                      <tr className="subtotal-do-grupo">
                        <td colSpan={3}>Subtotal desta nota</td>
                        <td className="tnum" data-teste="subtotal-do-grupo">{br(somar(grupo.linhas.map((l) => l.liberadaValor)))}</td>
                      </tr>
                    )}
                  </tbody>
                )
              })}
              <tfoot>
                <tr>
                  <td colSpan={3} className="rotulo-do-total">Total a faturar</td>
                  <td className="tnum total" data-teste="total-do-extrato">{br(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div className="modal-rodape">
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Fechar
          </button>
          <button
            type="button"
            className="primario"
            disabled={linhas.length === 0}
            data-teste="baixar-extrato"
            onClick={() => {
              // A folha de impressão (`@media print` em `portal.css`) reduz a página a
              // este painel. O que sai no papel são os mesmos números da tela.
              window.print()
            }}
          >
            {/* O ícone do "Exportar" do app interno (fluxo-de-caixa.tsx:319); o rótulo é o da DS-386. */}
            <Download size={16} aria-hidden="true" /> Salvar em PDF
          </button>
        </div>
    </ModalDoPortal>
  )
}
